import { motion } from 'framer-motion';
import {
    ArrowRight,
    Briefcase,
    CheckCircle,
    Clock,
    Globe,
    LogOut,
    Mail,
    MapPin,
    MessageSquare,
    Phone,
    Send,
    Star,
    Users
} from 'lucide-react';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Contact = () => {
  const { isAuthenticated, user, logout } = useAuth();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: '',
    inquiryType: 'general',
    userType: 'jobseeker'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogout = () => {
    logout();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate form
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      setLoading(false);
      return;
    }

    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      toast.error('Please enter a valid email address');
      setLoading(false);
      return;
    }

    // Simulate API call
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      toast.success('Message sent successfully! We\'ll get back to you within 24 hours.');
      setFormData({
        name: '',
        email: '',
        company: '',
        subject: '',
        message: '',
        inquiryType: 'general',
        userType: 'jobseeker'
      });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email Us',
      details: 'support@jobzentro.com',
      description: 'Send us an email and we\'ll respond within 24 hours',
      action: 'mailto:support@jobzentro.com',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Phone,
      title: 'Call Us',
      details: '+1 (555) 123-4567',
      description: 'Mon-Fri 9AM-6PM EST',
      action: 'tel:+15551234567',
      gradient: 'from-emerald-500 to-green-500'
    },
    {
      icon: MapPin,
      title: 'Visit Us',
      details: '123 Innovation Drive, Suite 400',
      description: 'San Francisco, CA 94107',
      action: 'https://maps.google.com',
      gradient: 'from-purple-500 to-violet-500'
    },
    {
      icon: MessageSquare,
      title: 'Live Chat',
      details: 'Available 24/7',
      description: 'Get instant help from our support team',
      action: '#',
      gradient: 'from-amber-500 to-orange-500'
    }
  ];

  const officeHours = [
    { day: 'Monday - Friday', hours: '9:00 AM - 6:00 PM EST' },
    { day: 'Saturday', hours: '10:00 AM - 4:00 PM EST' },
    { day: 'Sunday', hours: 'Closed' }
  ];

  const faqs = [
    {
      question: 'How do I create an account?',
      answer: 'Click on "Get Started" or "Sign Up" and fill out the registration form. You can create either a job seeker or employer account.'
    },
    {
      question: 'Is JobZentro free to use?',
      answer: 'Yes! JobZentro is completely free for job seekers. Employers can post jobs and access basic features for free, with premium plans available for advanced features.'
    },
    {
      question: 'How do I reset my password?',
      answer: 'Click on "Forgot Password" on the login page and enter your email address. We\'ll send you instructions to reset your password.'
    },
    {
      question: 'Can I edit my job posting after it\'s published?',
      answer: 'Yes, you can edit your job posting anytime from your recruiter dashboard. Changes will be reflected immediately.'
    },
    {
      question: 'How do I delete my account?',
      answer: 'You can delete your account from your profile settings. Please note that this action is irreversible.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Header */}
      <header className="nav-luxury sticky top-0 z-50">
        <div className="container-luxury">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-4">
              <Link to="/" className="flex items-center space-x-3 group">
                <div className="w-10 h-10 gradient-bg-luxury rounded-2xl flex items-center justify-center shadow-luxury group-hover:scale-105 transition-transform">
                  <Briefcase className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-display font-bold gradient-text">JobZentro</h1>
                  <p className="text-xs text-slate-500 font-medium">An AI HRMS Platform</p>
                </div>
              </Link>
              <nav className="hidden lg:flex space-x-8 ml-8">
                {isAuthenticated ? (
                  <>
                    <Link to="/dashboard" className="nav-link">Dashboard</Link>
                    <Link to="/jobs" className="nav-link">Browse Jobs</Link>
                    <Link to="/applications" className="nav-link">Applications</Link>
                    <Link to="/profile" className="nav-link">Profile</Link>
                    <Link to="/about" className="nav-link">About</Link>
                    <Link to="/contact" className="nav-link active">Contact</Link>
                  </>
                ) : (
                  <>
                    <Link to="/" className="nav-link">Home</Link>
                    <Link to="/jobs" className="nav-link">Jobs</Link>
                    <Link to="/about" className="nav-link">About</Link>
                    <Link to="/contact" className="nav-link active">Contact</Link>
                  </>
                )}
              </nav>
            </div>
            
            <div className="flex items-center space-x-4">
              {isAuthenticated ? (
                <>
                  <Link to="/profile" className="flex items-center space-x-3 p-2 rounded-xl hover:bg-slate-100 transition-colors">
                    <img
                      src={user?.profilePicture?.url || user?.profilePicture || `https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face`}
                      alt="Profile"
                      className="w-10 h-10 rounded-full object-cover shadow-soft"
                    />
                    <div className="hidden md:block text-left">
                      <p className="text-sm font-heading font-semibold text-slate-900">
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-xs text-slate-500">Job Seeker</p>
                    </div>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-2 text-slate-600 hover:text-slate-900 transition-colors p-2 rounded-xl hover:bg-slate-100"
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="hidden md:inline">Logout</span>
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">
                    Sign In
                  </Link>
                  <Link to="/register" className="btn-primary">
                    Get Started
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="section-luxury pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 hero-background opacity-10"></div>
        <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-blue-400/20 to-purple-600/20 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-br from-green-400/20 to-blue-600/20 rounded-full blur-3xl animate-pulse-luxury"></div>
        
        <div className="container-luxury relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-white/20 shadow-soft mb-8">
              <MessageSquare className="w-4 h-4 text-blue-500 mr-2" />
              <span className="text-sm font-medium text-slate-700">We're Here to Help</span>
            </div>
            
            <h1 className="text-5xl lg:text-6xl font-display font-bold leading-tight mb-6">
              Get in
              <span className="block gradient-text text-shadow-lg">Touch</span>
            </h1>
            
            <p className="text-xl text-slate-600 leading-relaxed mb-8 max-w-3xl mx-auto">
              Have a question, suggestion, or need assistance? We'd love to hear from you. 
              Our dedicated support team is here to help you succeed.
            </p>
            
            <div className="flex items-center justify-center space-x-8 text-sm text-slate-600">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>24-hour response time</span>
              </div>
              <div className="flex items-center space-x-2">
                <Star className="w-4 h-4 text-amber-400" />
                <span>Expert support team</span>
              </div>
              <div className="flex items-center space-x-2">
                <Users className="w-4 h-4 text-blue-500" />
                <span>Personal assistance</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="container-luxury py-8">
        {/* Contact Methods */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-16"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-display font-bold text-slate-900 mb-4">
              Multiple Ways to
              <span className="block gradient-text">Connect</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Choose the method that works best for you. We're available through multiple channels.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <motion.a
                  key={index}
                  href={info.action}
                  target={info.action.startsWith('http') ? '_blank' : '_self'}
                  rel={info.action.startsWith('http') ? 'noopener noreferrer' : ''}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="card-luxury p-6 text-center group hover:scale-105 transition-all duration-300 cursor-pointer"
                >
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${info.gradient} flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">{info.title}</h3>
                  <p className="text-lg font-medium gradient-text-primary mb-2">{info.details}</p>
                  <p className="text-slate-600 text-sm">{info.description}</p>
                </motion.a>
              );
            })}
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="card-luxury p-8"
            >
              <div className="mb-8">
                <h2 className="text-3xl font-display font-bold text-slate-900 mb-4">Send us a Message</h2>
                <p className="text-slate-600">
                  Fill out the form below and we'll get back to you as soon as possible.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="form-group">
                    <label className="form-label">Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="input-luxury"
                      placeholder="Your full name"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="input-luxury"
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="form-group">
                    <label className="form-label">Company</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="input-luxury"
                      placeholder="Your company name"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Inquiry Type</label>
                    <select
                      name="inquiryType"
                      value={formData.inquiryType}
                      onChange={handleInputChange}
                      className="input-luxury"
                    >
                      <option value="general">General Inquiry</option>
                      <option value="support">Technical Support</option>
                      <option value="billing">Billing Question</option>
                      <option value="partnership">Partnership</option>
                      <option value="feedback">Feedback</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Subject</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="input-luxury"
                    placeholder="Brief description of your inquiry"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={6}
                    className="input-luxury resize-none"
                    placeholder="Tell us more about your inquiry..."
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">I am a</label>
                  <div className="flex space-x-6">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="userType"
                        value="jobseeker"
                        checked={formData.userType === 'jobseeker'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-slate-700 font-medium">Job Seeker</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="userType"
                        value="employer"
                        checked={formData.userType === 'employer'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-slate-700 font-medium">Employer</span>
                    </label>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Sending Message...
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send className="w-4 h-4 ml-2" />
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Office Hours */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="card-luxury p-6"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-500 rounded-xl flex items-center justify-center shadow-lg">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-heading font-semibold text-slate-900">Office Hours</h3>
              </div>
              
              <div className="space-y-3">
                {officeHours.map((schedule, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-b-0">
                    <span className="text-slate-600 font-medium">{schedule.day}</span>
                    <span className="text-slate-900 font-semibold">{schedule.hours}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Quick Links */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="card-luxury p-6"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
                  <ArrowRight className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-heading font-semibold text-slate-900">Quick Links</h3>
              </div>
              
              <div className="space-y-3">
                <Link to="/jobs" className="flex items-center space-x-3 p-3 rounded-xl hover:bg-slate-50 transition-colors group">
                  <Briefcase className="w-5 h-5 text-slate-400 group-hover:text-blue-500 transition-colors" />
                  <span className="text-slate-700 group-hover:text-slate-900 font-medium">Browse Jobs</span>
                </Link>
                <Link to="/register" className="flex items-center space-x-3 p-3 rounded-xl hover:bg-slate-50 transition-colors group">
                  <Users className="w-5 h-5 text-slate-400 group-hover:text-blue-500 transition-colors" />
                  <span className="text-slate-700 group-hover:text-slate-900 font-medium">Create Account</span>
                </Link>
                <Link to="/about" className="flex items-center space-x-3 p-3 rounded-xl hover:bg-slate-50 transition-colors group">
                  <Globe className="w-5 h-5 text-slate-400 group-hover:text-blue-500 transition-colors" />
                  <span className="text-slate-700 group-hover:text-slate-900 font-medium">About Us</span>
                </Link>
              </div>
            </motion.div>

            {/* Support Promise */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="card-luxury p-6 bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Star className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-heading font-semibold text-slate-900 mb-3">Our Promise</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  We're committed to providing exceptional support. Every inquiry receives personalized attention from our expert team.
                </p>
              </div>
            </motion.div>
          </div>
        </div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-16"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-display font-bold text-slate-900 mb-4">
              Frequently Asked
              <span className="block gradient-text">Questions</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Find quick answers to common questions. Can't find what you're looking for? Contact us directly.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.7 + index * 0.1 }}
                className="card-luxury p-6"
              >
                <h3 className="text-lg font-heading font-semibold text-slate-900 mb-3">{faq.question}</h3>
                <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-slate-600 mb-4">Still have questions?</p>
            <a
              href="mailto:support@jobzentro.com"
              className="btn-outline"
            >
              <Mail className="w-4 h-4 mr-2" />
              Email Support
            </a>
          </div>
        </motion.div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16 mt-16">
        <div className="container-luxury">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            <div className="space-y-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 gradient-bg-luxury rounded-xl flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-display font-bold">JobZentro</h3>
                  <p className="text-xs text-slate-400">An AI HRMS Platform</p>
                </div>
              </div>
              <p className="text-slate-400 leading-relaxed">
                Connecting exceptional talent with outstanding opportunities through innovative technology and personalized service.
              </p>
            </div>
            
            <div>
              <h4 className="font-heading font-semibold text-lg mb-6">For Job Seekers</h4>
              <ul className="space-y-3">
                <li><Link to="/jobs" className="text-slate-400 hover:text-white transition-colors">Browse Jobs</Link></li>
                <li><Link to="/register" className="text-slate-400 hover:text-white transition-colors">Create Profile</Link></li>
                <li><Link to="/dashboard" className="text-slate-400 hover:text-white transition-colors">Dashboard</Link></li>
                <li><Link to="#" className="text-slate-400 hover:text-white transition-colors">Career Advice</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-heading font-semibold text-lg mb-6">For Employers</h4>
              <ul className="space-y-3">
                <li><Link to="#" className="text-slate-400 hover:text-white transition-colors">Post Jobs</Link></li>
                <li><Link to="#" className="text-slate-400 hover:text-white transition-colors">Employer Dashboard</Link></li>
                <li><Link to="#" className="text-slate-400 hover:text-white transition-colors">Search Talent</Link></li>
                <li><Link to="#" className="text-slate-400 hover:text-white transition-colors">Pricing</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-heading font-semibold text-lg mb-6">Company</h4>
              <ul className="space-y-3">
                <li><Link to="/about" className="text-slate-400 hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/contact" className="text-slate-400 hover:text-white transition-colors">Contact</Link></li>
                <li><Link to="#" className="text-slate-400 hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="#" className="text-slate-400 hover:text-white transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-slate-400 text-center md:text-left">
              &copy; 2024 JobZentro. All rights reserved. Built with ❤️ for professionals worldwide.
            </p>
            <div className="flex items-center space-x-6 mt-4 md:mt-0">
              <a href="#" className="text-slate-400 hover:text-white transition-colors">
                <Mail className="w-5 h-5" />
              </a>
              <a href="#" className="text-slate-400 hover:text-white transition-colors">
                <Phone className="w-5 h-5" />
              </a>
              <a href="#" className="text-slate-400 hover:text-white transition-colors">
                <MapPin className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Contact; 